//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingStatuses, PAGE_SIZE } from '@/constants/constants';
import { cleanColumnPreferences, deepCopy, getInitials } from '@/_utils/utils';
import { selectSavedSearches, selectUserPreferences } from '../selectors/authSelectors';
import { selectDataTableAppliedSearchName, selectDataTableSettings, selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import axios, { setToken, unsetToken } from '@/_utils/axios';
import { clearFilters, setAppliedSearch } from './dataTableSlice';
import { UserPreferences, AuthState, LoginResponse, Preferences, Search, UserProfile, UserProfileResponse } from './slice_types';
import { AxiosResponse } from 'axios';
import { getIsAltDateFormat } from '@/_utils/date';
import { RootState } from '@/redux/store';
import { DataTableConfig } from '@/__configs/config_types';

const defaultUserColors = ['#009FA8', '#A84A0C', '#074AA8', '#1673F5', '#e616f5', '#A816F5', '#08C264'];

// THUNKS
export const doLogin = createAsyncThunk('auth/login', async ({ email, password }: { email: string; password: string }) => {
  return axios.post('/auth/login/', { email, password }).then((res: AxiosResponse<LoginResponse>) => res.data);
});

export const getPreferences = createAsyncThunk('auth/getPreferences', async (userColors: string[], { dispatch }) => {
  return axios.get('/auth/profile/').then((res: AxiosResponse<LoginResponse>) => {
    const { color, preferences } = res.data?.data || {};
    const hasUpdatablePrefs = !color?.length || !userColors?.includes(color) || !preferences?.date_format || !preferences?.time_format;
    if (hasUpdatablePrefs) dispatch(handleFirstTimeLogin({ res, userColors }));
    return res.data?.data || {};
  });
});

export const handleFirstTimeLogin = createAsyncThunk(
  'auth/handleFirstTimeLogin',
  async ({ res, userColors }: { res: AxiosResponse<LoginResponse>; userColors: string[] }) => {
    let color = res.data?.data?.color;
    let date_format = res.data?.data?.preferences?.date_format;
    let time_format = res.data?.data?.preferences?.time_format;
    const isAltDateFormat = getIsAltDateFormat();
    if (!userColors) userColors = defaultUserColors;
    if (!color?.length || !userColors.includes(color)) {
      const colorIndex = Math.floor(Math.random() * userColors.length);
      color = userColors[colorIndex];
    }
    // for users in certain timezones, we want to automatically set their date and time formats to the alternate format
    if (!date_format) {
      date_format = isAltDateFormat ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
    }
    if (!time_format) {
      time_format = isAltDateFormat ? 'HH:mm' : 'hh:mm aa';
    }
    return axios
      .post('/auth/profile/', { preferences: { date_format, time_format }, color })
      .then((res: AxiosResponse<UserProfileResponse>) => res.data.data);
  }
);

export const getAppVersion = createAsyncThunk('auth/getAppVersion', async () => {
  return axios
    .get('/common/version/')
    .then((res: AxiosResponse<any>) => res.data)
    .catch(() => {
      console.warn('Could not get app version');
    });
});

export const getSiteObject = createAsyncThunk('auth/getSiteObject', async () => {
  return axios
    .get('/common/site/')
    .then((res: AxiosResponse<any>) => res.data)
    .catch(() => {
      console.warn('Could not get site object');
    });
});

export const authCheck = createAsyncThunk('auth/authCheck', async () => {
  return axios.get('/auth/health/').then((res: AxiosResponse<UserProfileResponse>) => res.data);
});

export const savePreferences = createAsyncThunk('auth/savePreferences', async (newPrefs: any, thunkAPI) => {
  const prefs = (thunkAPI.getState() as any).auth?.preferences || {};
  const color = (thunkAPI.getState() as any).auth?.user.color;
  const preferences: UserPreferences = {
    ...prefs,
    ...newPrefs,
    // TODO: these relics are retired preferences we no longer use, can remove after some time
    // this is just for some clean up on profiles, but harmless if they are not removed
    dateFormat: null, // relic
    timeFormat: null, // relic
    editTasks: null, // relic
    reviewTasks: null // relic
  };
  return axios.post('/auth/profile/', { preferences, color }).then((res: AxiosResponse<UserProfileResponse>) => res.data?.data?.preferences || {});
}) as any;

export const toggleDarkMode = createAsyncThunk('auth/toggleDarkMode', async (dark_mode: boolean, thunkAPI) =>
  thunkAPI.dispatch(savePreferences({ dark_mode }))
);

export const toggleFullWidth = createAsyncThunk('auth/toggleFullWidth', async (tableKey: keyof DataTableConfig, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  let fullWidthMode =
    state.auth.preferences?.full_width_mode && typeof state.auth.preferences?.full_width_mode !== 'boolean'
      ? [...state.auth.preferences.full_width_mode]
      : [];

  if (fullWidthMode.length === 0) {
    fullWidthMode.push(tableKey);
  } else {
    const isInArray = fullWidthMode.includes(tableKey);
    if (isInArray) {
      fullWidthMode = fullWidthMode.filter((key) => key !== tableKey);
    } else {
      fullWidthMode.push(tableKey);
    }
  }

  return thunkAPI.dispatch(savePreferences({ full_width_mode: fullWidthMode }));
});

export const updatePageSize = createAsyncThunk('auth/updatePageSize', async (page_size: number, thunkAPI) =>
  thunkAPI.dispatch(savePreferences({ page_size }))
);

export const saveSearch = createAsyncThunk('auth/saveSearch', async (name: string, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const prefs = selectUserPreferences(state);
  const key = selectCurrentTableKey(state);
  const { filters, sorting } = selectDataTableSettings(state);
  const saved_searches = [...selectSavedSearches(state), { name, filters, sorting }];
  // TODO: update so we're not copying prefs each time
  if (!key) return;
  thunkAPI.dispatch(savePreferences({ [key]: { ...prefs[key], saved_searches } })).then(() => thunkAPI.dispatch(setAppliedSearch(name)));
});

export const editSavedSearch = createAsyncThunk(
  'auth/editSavedSearch',
  async (
    {
      previousName,
      newName
    }: {
      previousName: string;
      newName: string;
    },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as RootState;
    const prefs = selectUserPreferences(state);
    const key = selectCurrentTableKey(state);
    const saved_searches = deepCopy(selectSavedSearches(state));
    const savedSearch = saved_searches.find((search: any) => search.name === previousName);
    savedSearch.name = newName;
    // TODO: update so we're not copying prefs each time
    if (!key) return;
    return thunkAPI.dispatch(savePreferences({ [key]: { ...prefs[key], saved_searches } })).then(() => thunkAPI.dispatch(setAppliedSearch(newName)));
  }
);

export const deleteSavedSearch = createAsyncThunk('auth/deleteSavedSearch', async (name: string, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const prefs = selectUserPreferences(state);
  const key = selectCurrentTableKey(state);
  const appliedSearchName = selectDataTableAppliedSearchName(state);
  let saved_searches = selectSavedSearches(state);
  saved_searches = saved_searches.filter((search: Search) => search.name !== name);
  if (appliedSearchName === name) thunkAPI.dispatch(clearFilters(undefined));
  if (!key) return;
  return thunkAPI.dispatch(savePreferences({ [key]: { ...prefs[key], saved_searches } }));
});

export const undoDeleteSavedSearch = createAsyncThunk('auth/undoDeleteSavedSearch', async (search: Search, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const prefs = selectUserPreferences(state);
  const key = selectCurrentTableKey(state);
  const { filters, sorting } = search;
  const saved_searches = [...selectSavedSearches(state), { name: search.name, filters, sorting }];
  // TODO: update so we're not copying prefs each time
  if (!key) return;
  thunkAPI.dispatch(savePreferences({ [key]: { ...prefs[key], saved_searches } })).then(() => thunkAPI.dispatch(setAppliedSearch(search.name)));
});

function setUser(state: AuthState, user: UserProfile) {
  state.email = user.email;
  state.user = user;
  state.user.initials = getInitials(user);
  state.preferences = {
    ...state.preferences,
    dark_mode: user.preferences?.dark_mode || true,
    page_size: user.preferences?.page_size || PAGE_SIZE,
    ...cleanColumnPreferences(user.preferences)
  };
  state.permissions = user.user_permissions?.map((p) => p.codename) || [];
}

const initialState: AuthState = {
  initFinished: false,
  email: null,
  status: LoadingStatuses.fulfilled,
  user: {
    email: null,
    permissions: null,
    id: 0,
    first_name: null,
    last_name: null,
    groups: null,
    user_permissions: [],
    username: '',
    color: '',
    customer_associations: [],
    is_active: false
  } as UserProfile,
  preferences: {
    dark_mode: true,
    page_size: PAGE_SIZE,
    full_width_mode: []
  },
  permissions: [],
  routes: [],
  appVersion: null,
  site: null,
  timeZone: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    doLogout: (state) => {
      state.email = null;
      state.user = initialState.user;
      state.preferences = initialState.preferences;
      state.permissions = initialState.permissions;
      unsetToken();
    },
    updateTimeZone: (state, action: PayloadAction<string>) => {
      state.timeZone = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(doLogin.pending, (state: AuthState) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(doLogin.fulfilled, (state: AuthState, { payload }: PayloadAction<LoginResponse>) => {
      const { token, ...user } = payload.data;
      setToken(token);
      setUser(state, user);
      state.status = LoadingStatuses.fulfilled;
      state.initFinished = true;
    });
    builder.addCase(doLogin.rejected, (state) => {
      state.status = LoadingStatuses.rejected;
    });
    builder.addCase(getPreferences.fulfilled, (state: AuthState, { payload }: PayloadAction<UserProfile>) => {
      setUser(state, payload);
      state.initFinished = true;
    });
    builder.addCase(getPreferences.rejected, (state: AuthState) => {
      doLogout();
      state.initFinished = true;
    });
    builder.addCase(savePreferences.pending, (state: AuthState) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(savePreferences.fulfilled, (state: AuthState, { payload }: PayloadAction<Preferences>) => {
      state.status = LoadingStatuses.fulfilled;
      state.preferences = payload;
    });
    builder.addCase(handleFirstTimeLogin.fulfilled, (state: AuthState, { payload }) => {
      setUser(state, payload);
    });
    builder.addCase(getAppVersion.fulfilled, (state: AuthState, { payload }) => {
      state.appVersion = payload?.data;
    });
    builder.addCase(getSiteObject.fulfilled, (state: AuthState, { payload }) => {
      state.site = payload?.data;
    });
  }
});

export const initializer = (store: any) => {
  // calls endpoint every 4 hs to check if token is valid, if 401 interceptor will kick user out
  const fourHours = 4 * 60 * 60 * 1000;
  setInterval(() => store.dispatch(authCheck()), fourHours);
};

export const { doLogout, updateTimeZone } = authSlice.actions;

export default authSlice.reducer;
